import dayjs from 'dayjs';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { MenuItem, MenuItemDivider } from '@carbon/react';
import { Asleep, Checkmark, Email, Language, Logout, Settings } from '@carbon/react/icons';
import { useAuthActions } from 'src/hooks/useAuthActions';
import { useAuthSession } from 'src/hooks/useAuthSession';
import { useAppStore } from 'src/states/app';
import { EntityAvatar } from './EntityAvatar';
import { HeaderSwitcher } from './HeaderSwitcher';

export const UserMenu = React.memo(() => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthSession();
  const { signOut } = useAuthActions();
  const { theme, locale, setTheme, setLocale } = useAppStore(
    (state) => ({ theme: state.theme, locale: state.locale, setTheme: state.setTheme, setLocale: state.setLocale }),
    shallow
  );

  const changeLocale = useCallback(
    (next: 'vi' | 'en') => {
      setLocale(next);
      dayjs.locale(next);
      void i18n.changeLanguage(next);
    },
    [setLocale, i18n]
  );

  if (!user) {
    return null;
  }

  // Header real estate is tight (the search is absolutely centred), and the reference
  // design shows a bare avatar — no name, no chevron. `--header-user` hides both the same
  // way `--rail` used to when the side nav was collapsed; see index.scss.
  return (
    <HeaderSwitcher
      id="current-user-name"
      label={t('header.account')}
      text={user.name}
      className="shell-switcher--header-user"
      renderMark={() => <EntityAvatar name={user.name} size={28} />}
    >
      <MenuItem label={user.email} renderIcon={Email} disabled />
      <MenuItemDivider />
      <MenuItem label={t('header.profileSettings')} renderIcon={Settings} onClick={() => navigate('/profile')} />
      <MenuItem
        id="switch-theme-button"
        label={t('header.darkMode')}
        renderIcon={theme === 'dark' ? Checkmark : Asleep}
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      />
      <MenuItemDivider />
      <MenuItem
        id="switch-locale-vi"
        label="Tiếng Việt"
        renderIcon={locale === 'vi' ? Checkmark : Language}
        onClick={() => changeLocale('vi')}
      />
      <MenuItem
        id="switch-locale-en"
        label="English"
        renderIcon={locale === 'en' ? Checkmark : Language}
        onClick={() => changeLocale('en')}
      />
      <MenuItemDivider />
      <MenuItem label={t('header.signOut')} kind="danger" renderIcon={Logout} onClick={signOut} />
    </HeaderSwitcher>
  );
});
